import React, {useState} from 'react';
import {Select} from 'antd';
import {enumKeys, sortObjectKeysByValue} from 'lib/utils';
import {Acquirer, PaymentMethods, useChargeSourcesQuery} from 'api/generated';
import {acquirerLabels, billingPlans, paymentMethodLabels} from 'lib/labels';
import SkeletonInput from 'antd/es/skeleton/Input';
import {SearchBar} from 'components';
import {StringParam, useQueryParams} from 'use-query-params';
import {QueryParamConfig} from 'serialize-query-params/lib/types';
import {FilterByValue, FilterType} from './Dashboard';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {useMasterAccounts} from 'hooks/useMasterAccounts';

type FilterProps = {
  filterBy: FilterType;
};

const inputStyle = {width: 400};

const FilterInput: React.FC<FilterProps> = ({filterBy}) => {
  const {data: chargeSourcesData, loading: loadingChargeSources} = useChargeSourcesQuery();
  const chargeSources = chargeSourcesData?.chargeSources || [];

  const [{acquirer, masterAccountId, billingPlan, paymentMethod, source}, setQuery] =
    useQueryParams({
      acquirer: StringParam as QueryParamConfig<string | undefined>,
      masterAccountId: StringParam as QueryParamConfig<string | undefined>,
      billingPlan: StringParam as QueryParamConfig<string | undefined>,
      paymentMethod: StringParam as QueryParamConfig<string | undefined>,
      source: StringParam as QueryParamConfig<string | undefined>
    });

  const getFilterValue = () => {
    return acquirer || masterAccountId || billingPlan || paymentMethod || source || undefined;
  };

  const [filterValue, setFilterValue] = useState(getFilterValue);

  const onChange = (filter?: FilterType, value?: string) => {
    setFilterValue(value);
    setQuery({
      acquirer: undefined,
      masterAccountId: undefined,
      billingPlan: undefined,
      paymentMethod: undefined,
      source: undefined,
      ...(filter ? {[filter]: value} : {})
    });
  };

  const {selectOptions, loading} = useMasterAccounts();

  switch (filterBy) {
    case 'acquirer':
      return (
        <Select
          style={inputStyle}
          placeholder="Select acquirer"
          value={filterValue}
          onChange={(value) => onChange('acquirer', value)}
          allowClear
          showSearch
          optionFilterProp="children">
          {enumKeys(Acquirer).map((method) => (
            <Select.Option key={method} value={method}>
              {acquirerLabels[method]}
            </Select.Option>
          ))}
        </Select>
      );
    case 'masterAccountId':
      return !selectOptions ? (
        <SkeletonInput />
      ) : (
        <Select
          style={inputStyle}
          allowClear
          showSearch
          optionFilterProp="children"
          value={filterValue}
          loading={loading}
          disabled={loading}
          placeholder="Select master account"
          onChange={(value) => onChange('masterAccountId', value)}>
          {selectOptions.map((option: {label: string; value: string}) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      );
    case 'billingPlan':
      return (
        <Select
          style={inputStyle}
          placeholder="Select billing plan"
          allowClear
          value={filterValue}
          showSearch
          optionFilterProp="children"
          onChange={(value) => onChange('billingPlan', value)}>
          {enumKeys(billingPlans).map((plan) => (
            <Select.Option key={plan} value={plan}>
              {billingPlans[plan]}
            </Select.Option>
          ))}
        </Select>
      );
    case 'paymentMethod':
      return (
        <Select
          style={inputStyle}
          placeholder="Select payment method"
          showSearch
          optionFilterProp="children"
          value={filterValue}
          allowClear
          onChange={(value) => onChange('paymentMethod', value)}>
          {Object.values(PaymentMethods)
            .filter((m) => paymentMethodLabels[m])
            .map((method) => (
              <Select.Option key={method} value={method}>
                {paymentMethodLabels[method]}
              </Select.Option>
            ))}
        </Select>
      );
    case 'source':
      return (
        <Select
          style={inputStyle}
          placeholder="Select payment source"
          showSearch
          optionFilterProp="children"
          allowClear
          loading={loadingChargeSources}
          value={filterValue}
          onChange={(value) => onChange('source', value ? 'MONEI/' + value : undefined)}>
          {chargeSources.map((source) => (
            <Select.Option key={source} value={source}>
              {source}
            </Select.Option>
          ))}
        </Select>
      );
  }
  return <></>;
};

export const DashboardFilters: React.FC<FilterByValue> = ({filterBy}) => {
  const [selectedFilter, setSelectedFilter] = useState<FilterType>(filterBy as FilterType);

  const {isAdmin} = useAccessLevel();

  let availableFilters: Record<string, string> = {
    ...(isAdmin ? {acquirer: 'Acquirer'} : {}),
    ...(isAdmin ? {masterAccountId: 'Master account'} : {}),
    ...(isAdmin ? {billingPlan: 'Billing plan'} : {}),
    paymentMethod: 'Payment method',
    ...(isAdmin ? {source: 'Source'} : {})
  };

  availableFilters = sortObjectKeysByValue(availableFilters);

  return (
    <SearchBar compact>
      <Select
        style={{minWidth: 250}}
        value={selectedFilter}
        onChange={setSelectedFilter}
        placeholder="Filter by...">
        {Object.entries(availableFilters).map(([value, label]) => {
          return (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          );
        })}
      </Select>
      {selectedFilter && <FilterInput filterBy={selectedFilter} />}
    </SearchBar>
  );
};
